import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken } from './authToken';

type Props = { API: string; onChange?: () => void };
const auth = () => ({ Authorization: 'Bearer ' + getAuthToken() });
const DEFAULT_URL = 'https://marketplace.empik.com';

const inp: any = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl: any = { fontSize: 12, color: '#6b7280', marginBottom: 4 };
const errBox: any = { background: '#fef2f2', border: '1px solid #fecdd3', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 };
const okBox: any = { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 };

const SHOP_STATE: Record<string, string> = { OPEN: 'otwarty', CLOSE: 'zamknięty', SUSPENDED: 'zawieszony' };

export default function EmpikSettings({ API, onChange }: Props) {
  const [st, setSt] = useState<any>(null);
  const [form, setForm] = useState({ url: DEFAULT_URL, shop_id: '', api_key: '' });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const errOf = (e: any, d: string) => (e.response && e.response.data && e.response.data.detail) || d;

  const load = () => {
    axios.get(API + '/empik-settings', { headers: auth() })
      .then((r: any) => {
        setSt(r.data);
        setForm((f) => ({ ...f, url: r.data.url || DEFAULT_URL, shop_id: r.data.shop_id || '', api_key: '' }));
      })
      .catch(() => setSt(null));
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const save = () => {
    setBusy(true); setMsg(''); setErr('');
    axios.post(API + '/empik-settings', form, { headers: auth() })
      .then((r: any) => {
        setSt(r.data); setForm((f) => ({ ...f, shop_id: r.data.shop_id || '', api_key: '' }));
        setMsg('Połączono z Empikiem.'); if (onChange) onChange();
      })
      .catch((e: any) => setErr(errOf(e, 'Nie udało się połączyć z Empikiem.')))
      .finally(() => setBusy(false));
  };

  const test = () => {
    setTesting(true); setMsg(''); setErr('');
    axios.post(API + '/empik-settings/test', {}, { headers: auth() })
      .then(() => { setMsg('Połączenie działa.'); load(); })
      .catch((e: any) => setErr(errOf(e, 'Test nieudany.')))
      .finally(() => setTesting(false));
  };

  const setAuto = (on: boolean) => {
    setMsg(''); setErr('');
    axios.post(API + '/empik-settings/options', { auto_accept: on }, { headers: auth() })
      .then((r: any) => { setSt(r.data); setMsg(on ? 'Zamówienia Empik będą akceptowane automatycznie.' : 'Zamówienia Empik będą akceptowane ręcznie.'); })
      .catch((e: any) => setErr(errOf(e, 'Nie udało się zapisać ustawienia.')));
  };

  const disconnect = () => {
    if (!window.confirm('Odłączyć Empik od OPENkontakt? Klucz API zostanie usunięty.')) return;
    setMsg(''); setErr('');
    axios.delete(API + '/empik-settings', { headers: auth() })
      .then(() => { setMsg('Empik odłączony.'); load(); if (onChange) onChange(); })
      .catch((e: any) => setErr(errOf(e, 'Nie udało się odłączyć.')));
  };

  const connected = !!(st && st.connected);
  const acc = (st && st.account) || {};

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Empik Marketplace</div>
      <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
        Połączenie z kontem sprzedawcy Empik (platforma Mirakl). Shop ID znajdziesz w panelu Empik na Dashboardzie („Identifier”).
      </div>
      <div style={{ fontSize: 13, color: '#1e3a8a', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
        <b>Gdzie znaleźć klucz API:</b> w panelu Empik kliknij nazwę użytkownika w prawym górnym rogu → <b>Ustawienia osobiste</b> → <b>Klucz API</b>.
      </div>
      {err ? <div style={errBox}>{err}</div> : null}
      {msg ? <div style={okBox}>{msg}</div> : null}

      {connected ? (
        <div style={{ fontSize: 13, color: '#374151', marginBottom: 14, lineHeight: 1.6 }}>
          <div>Sklep: <b>{String(acc.shop_name || '—')}</b> · Shop ID <code>{String(st.shop_id || '—')}</code>{acc.shop_state ? <> · stan: {SHOP_STATE[acc.shop_state] || String(acc.shop_state)}</> : null}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>
            {acc.offers_count != null ? <>Oferty: {String(acc.offers_count)} · </> : null}
            {acc.orders_count != null ? <>Zamówienia: {String(acc.orders_count)} · </> : null}
            {acc.currency ? <>Waluta: {String(acc.currency)} · </> : null}
            Sprawdzono: {acc.checked_at ? new Date(acc.checked_at).toLocaleString('pl-PL') : '—'}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={test} disabled={testing} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer', opacity: testing ? 0.5 : 1 }}>{testing ? '⏳ Testowanie…' : 'Testuj połączenie'}</button>
            <button onClick={disconnect} style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #fecdd3', background: '#fff', color: '#b91c1c', fontSize: 12, cursor: 'pointer' }}>Odłącz</button>
          </div>
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10, maxWidth: 620 }}>
        <div style={{ gridColumn: '1 / 3' }}>
          <div style={lbl}>Adres panelu Empik (instancja Mirakl)</div>
          <input value={form.url} onChange={(e: any) => setForm((f) => ({ ...f, url: e.target.value }))} autoComplete="off" placeholder={DEFAULT_URL} style={inp} />
        </div>
        <div>
          <div style={lbl}>Shop ID</div>
          <input value={form.shop_id} onChange={(e: any) => setForm((f) => ({ ...f, shop_id: e.target.value.replace(/\D/g, '') }))} autoComplete="off" placeholder="puste = sklep domyślny klucza" style={inp} />
        </div>
        <div>
          <div style={lbl}>Klucz API</div>
          <input type="password" value={form.api_key} onChange={(e: any) => setForm((f) => ({ ...f, api_key: e.target.value }))} autoComplete="new-password"
            placeholder={connected ? 'zapisany — wpisz, aby zmienić' : 'klucz API z profilu Empik'} style={inp} />
        </div>
      </div>
      <button onClick={save} disabled={busy || (!connected && !form.api_key.trim())}
        style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid #1d4ed8', background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (busy || (!connected && !form.api_key.trim())) ? 0.5 : 1 }}>
        {busy ? '⏳ Łączenie…' : (connected ? 'Zapisz i sprawdź' : 'Połącz z Empikiem')}
      </button>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>Klucz API jest przechowywany w zaszyfrowanej postaci i nie jest wyświetlany po zapisaniu. Zapis od razu sprawdza połączenie (odczyt danych sklepu, nic nie zmienia w Empiku).</div>

      <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 14, paddingTop: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Zamówienia Empik</div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer', marginBottom: 6 }}>
          <input type="radio" name="empik_accept" checked={!!(st && st.auto_accept)} onChange={() => setAuto(true)} disabled={!st} />
          <span><b>Akceptuj automatycznie</b> przy pobraniu zamówienia</span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer' }}>
          <input type="radio" name="empik_accept" checked={!!(st && !st.auto_accept)} onChange={() => setAuto(false)} disabled={!st} />
          <span><b>Akceptuj ręcznie</b> przyciskiem w zamówieniu</span>
        </label>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>Pobieranie zamówień z Empiku zostanie włączone w kolejnym etapie. Ustawienie zapisuje się od razu.</div>
      </div>
    </div>
  );
}
